import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Mic } from 'lucide-react'
import { Field, useLeaveGuard } from '../components/Form'
import { Screen } from '../components/Screen'
import { EmptyState } from '../components/States'
import { useEnv } from '../context'
import { useInvalidateSessionMarks, useSessionBoard } from '../data/hooks'
import {
  BLOCKED_TEXT,
  MASS_TEXT,
  ROLL_VOICE_PROBLEM,
  confirmOutcome,
  formatSeconds,
  markText,
  previewProblem,
  transcribeProblem,
  type ConfirmOutcome,
  type RollCallProblem,
} from '../data/rollCall'
import { formatDayShort, parseIsoDate, parseSessionKey } from '../data/schedule'
import { SliceApiError, type RollCallPreview, type RollCallRow } from '../data/types'
import { useVoiceInput } from '../data/useVoiceInput'
import { useOnline } from '../platform/network'

type Phase = 'compose' | 'transcribing' | 'parsing' | 'preview' | 'saving' | 'done'

export interface RollCallAudio {
  blob: Blob
  durationMs: number
}

/**
 * Перекличка текстом и голосом на экране занятия (этап 5, шаг 2, SC03).
 *
 * Фразу («Иванов, Петрова есть, Сидоров болеет, остальных нет») разбирает
 * сервер: модель раскладывает её по отметкам, имена сопоставляются с
 * составом группы. Решение владельца 2026-10-06:
 * - короткая перекличка (названы конкретные дети) записывается сразу, как
 *   тап по строкам: экран показывает итог и непонятые имена для ручной
 *   отметки. Повтор той же фразы ничего не добавляет;
 * - массовая («остальных нет», «все, кроме», «все здесь»), в том числе вместе
 *   с именами, - весь состав «было -> станет» и запись только после
 *   «Записать отметки», ровно один раз: повторное нажатие, второе окно или
 *   бот не пишут второй раз (отложенное действие этапа 3).
 * Разбор не тратит запросы Штаба.
 *
 * Без сети ничего не копится (решение 7а): голос можно записать, но
 * распознать и разобрать - только с сетью и по нажатию. Это не Штаб с его
 * очередью.
 */
export function SessionRollCallScreen({ initialAudio, onClose }: { initialAudio?: RollCallAudio; onClose?: () => void } = {}) {
  const { key = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { api, platform } = useEnv()
  const online = useOnline()
  const parsed = parseSessionKey(key)
  const board = useSessionBoard(key)
  const invalidate = useInvalidateSessionMarks(parsed?.groupId)

  const [phase, setPhase] = useState<Phase>('compose')
  const [text, setText] = useState('')
  const [audio, setAudio] = useState<RollCallAudio | null>(initialAudio ?? null)
  const initialAudioHandled = useRef(false)
  const [problem, setProblem] = useState<RollCallProblem | null>(null)
  const [preview, setPreview] = useState<(RollCallPreview & { heard: string }) | null>(null)
  const [saveError, setSaveError] = useState<'offline' | 'server' | null>(null)
  const [outcome, setOutcome] = useState<ConfirmOutcome | null>(null)
  const confirming = useRef(false)
  const pendingRef = useRef<string | undefined>(undefined)
  pendingRef.current = phase === 'preview' ? preview?.pendingActionId : undefined

  // Ушли, не подтвердив: подготовленное действие отменяется, чтобы не
  // висело полчаса. Ничего не записано и так.
  useEffect(
    () => () => {
      const id = pendingRef.current
      if (id) void api.cancelAiAction(id).catch(() => {})
    },
    [api],
  )

  const dirty = phase !== 'done' && (!!text.trim() || !!audio)
  const leave = useLeaveGuard(dirty)
  const back = useCallback(() => {
    leave.guard(() => {
      if (onClose) onClose()
      else if (location.key !== 'default') navigate(-1)
      else navigate(`/sessions/${encodeURIComponent(key)}`, { replace: true })
    })
  }, [key, leave, location.key, navigate, onClose])

  const parse = useCallback(
    async (phrase: string) => {
      const trimmed = phrase.trim()
      if (!trimmed) return
      setProblem(null)
      setSaveError(null)
      setOutcome(null)
      setPhase('parsing')
      try {
        const res = await api.previewRollCall(key, trimmed)
        setPreview({ ...res, heard: trimmed })
        if (res.recorded) {
          // Короткая перекличка уже записана - как тап по строкам
          platform.haptic('success')
          setOutcome({ ok: true, reparse: false, text: `Записано отметок: ${res.changes}.` })
          await invalidate()
          setPhase('done')
          return
        }
        setPhase('preview')
      } catch (err) {
        setProblem(previewProblem(err))
        setPhase('compose')
      }
    },
    [api, invalidate, key, platform],
  )

  const transcribe = useCallback(
    async (blob: Blob) => {
      setProblem(null)
      setPhase('transcribing')
      try {
        const heard = await api.transcribeRollCall(key, blob)
        if (!heard) {
          setAudio(null)
          setProblem({ kind: 'retry', text: 'Не распознано: слов не разобрать. Запишите ещё раз или напишите текстом.' })
          setPhase('compose')
          return
        }
        setAudio(null)
        setText(heard)
        await parse(heard)
      } catch (err) {
        const p = transcribeProblem(err)
        // Без сети запись остаётся на экране; «не распознано» - выбрасываем
        if (p.kind !== 'offline' && err instanceof SliceApiError && err.message === 'transcription failed') setAudio(null)
        setProblem(p)
        setPhase('compose')
      }
    },
    [api, key, parse],
  )

  const recorder = useVoiceInput((blob, durationMs) => {
    setAudio({ blob, durationMs })
    // Распознаём сразу по «Готово», если есть сеть. Без сети - ждём нажатия.
    if (online) void transcribe(blob)
  })

  // Запись со списка передаётся только в памяти, без URL и истории браузера.
  // Без сети ждём явного «Распознать», как и при записи на отдельном экране.
  useEffect(() => {
    if (!initialAudio || initialAudioHandled.current) return
    initialAudioHandled.current = true
    if (online) void transcribe(initialAudio.blob)
  }, [initialAudio, online, transcribe])

  const fix = () => {
    const id = preview?.pendingActionId
    if (id) void api.cancelAiAction(id).catch(() => {})
    setPreview(null)
    setOutcome(null)
    setSaveError(null)
    setPhase('compose')
  }

  const confirm = async () => {
    const id = preview?.pendingActionId
    if (!id || confirming.current) return
    confirming.current = true
    setSaveError(null)
    setPhase('saving')
    try {
      const decision = await api.confirmAiAction(id)
      const result = confirmOutcome(decision.action, decision.replayed)
      setOutcome(result)
      if (result.ok) {
        platform.haptic('success')
        await invalidate()
        setPhase('done')
      } else {
        platform.haptic('error')
        setPhase('preview')
      }
    } catch (err) {
      platform.haptic('error')
      setSaveError(err instanceof SliceApiError && err.kind === 'network' ? 'offline' : 'server')
      setPhase('preview')
    } finally {
      confirming.current = false
    }
  }

  const toSession = () => {
    if (onClose) onClose()
    else if (location.key !== 'default') navigate(-1)
    else navigate(`/sessions/${encodeURIComponent(key)}`, { replace: true })
  }

  const date = parsed ? parseIsoDate(parsed.date) : null
  const subtitle =
    board.data && date ? `${board.data.group_name} / ${formatDayShort(date)}${parsed?.startTime ? ` / ${parsed.startTime}` : ''}` : undefined

  if (!parsed) {
    return (
      <Screen title="Перекличка" back={back}>
        <EmptyState title="Ссылка на занятие устарела или неверна" text="Откройте занятие из расписания." />
      </Screen>
    )
  }

  const busy = phase === 'transcribing' || phase === 'parsing' || phase === 'saving'
  const recording = recorder.phase === 'recording'

  let actionBar: ReactNode
  if (phase === 'compose' || phase === 'transcribing' || phase === 'parsing') {
    actionBar = (
      <button
        type="button"
        className="pc-btn pc-btn--primary pc-btn--block"
        disabled={!text.trim() || busy || !online || recorder.phase !== 'idle'}
        onClick={() => void parse(text)}
      >
        {phase === 'parsing' ? 'Разбираю…' : phase === 'transcribing' ? 'Распознаю…' : 'Разобрать'}
      </button>
    )
  } else if (phase === 'preview' || phase === 'saving') {
    const canConfirm = !!preview?.pendingActionId && !outcome?.reparse
    actionBar = (
      <>
        {canConfirm && (
          <button
            type="button"
            className="pc-btn pc-btn--primary"
            style={{ flex: '3 1 0' }}
            disabled={phase === 'saving'}
            onClick={() => void confirm()}
          >
            {phase === 'saving' ? 'Записываю…' : saveError ? 'Повторить запись' : `Записать отметки: ${preview?.changes}`}
          </button>
        )}
        {outcome?.reparse && (
          <button type="button" className="pc-btn pc-btn--primary" style={{ flex: '3 1 0' }} onClick={() => void parse(preview?.heard ?? text)}>
            Разобрать заново
          </button>
        )}
        <button
          type="button"
          className={`pc-btn${canConfirm || outcome?.reparse ? '' : ' pc-btn--primary'}`}
          style={{ flex: '2 1 0' }}
          disabled={phase === 'saving'}
          onClick={fix}
        >
          Исправить
        </button>
      </>
    )
  } else {
    actionBar = (
      <button type="button" className="pc-btn pc-btn--primary pc-btn--block" onClick={toSession}>
        К занятию
      </button>
    )
  }

  return (
    // Штаб в шапке здесь не нужен (разбор фразы и так идёт через сервер), а на
    // 320 px он переносил заголовок по буквам
    <Screen title="Перекличка" subtitle={subtitle} back={back} actionBar={actionBar} hideHub>
      {board.data?.cancelled ? (
        <EmptyState title="Занятие отменено" text="Отметки для отменённого занятия не ставятся." />
      ) : phase === 'done' && outcome ? (
        <Done outcome={outcome} preview={preview} />
      ) : phase === 'preview' || phase === 'saving' ? (
        preview && <PreviewView preview={preview} outcome={outcome} saveError={saveError} online={online} />
      ) : (
        <>
          <p className="pc-muted" style={{ margin: '12px 0 0', fontSize: 14 }}>
            Скажите или напишите, кто есть и кого нет. Названные отметки записываются сразу, как тапом. «Остальных
            нет», «все, кроме» и «все здесь» - сначала покажу весь список и попрошу подтвердить.
          </p>
          <div className="pc-form" style={{ marginTop: 12 }}>
            <Field label="Кто есть и кого нет" hint="Например: Иванов, Петрова есть. Сидоров болеет. Остальных нет.">
              {(p) => (
                <textarea
                  {...p}
                  className="pc-input"
                  rows={4}
                  value={text}
                  disabled={busy}
                  onChange={(e) => {
                    setText(e.target.value)
                    if (problem?.kind !== 'offline') setProblem(null)
                  }}
                />
              )}
            </Field>
          </div>

          <div className="pc-hub-voice">
            {recorder.phase === 'idle' && !audio && (
              <button type="button" className="pc-btn" disabled={busy} onClick={() => void recorder.start()}>
                <Mic size={18} aria-hidden="true" />
                Сказать голосом
              </button>
            )}
            {recorder.phase === 'starting' && (
              <>
                <span className="pc-muted">Жду разрешения на микрофон</span>
                <button type="button" className="pc-btn" onClick={recorder.cancel}>
                  Отменить
                </button>
              </>
            )}
            {recording && (
              <>
                <span className="pc-hub-rec" role="status">
                  <span className="pc-hub-rec__dot" aria-hidden="true" />
                  Идёт запись {formatSeconds(recorder.seconds)}
                </span>
                <button type="button" className="pc-btn" onClick={recorder.cancel}>
                  Отменить
                </button>
                <button type="button" className="pc-btn pc-btn--primary" onClick={recorder.stop}>
                  Готово
                </button>
              </>
            )}
            {recorder.phase === 'idle' && audio && phase === 'compose' && (
              <>
                <span className="pc-hub-rec" role="status">
                  Запись {formatSeconds(Math.max(1, Math.round(audio.durationMs / 1000)))} не распознана
                </span>
                <button type="button" className="pc-btn" onClick={() => setAudio(null)}>
                  Удалить запись
                </button>
                <button
                  type="button"
                  className="pc-btn pc-btn--primary"
                  disabled={!online}
                  onClick={() => void transcribe(audio.blob)}
                >
                  Распознать
                </button>
              </>
            )}
          </div>

          <div aria-live="polite">
            {phase === 'transcribing' && <p className="pc-muted">Распознаю голос…</p>}
            {phase === 'parsing' && <p className="pc-muted">Разбираю перекличку…</p>}
          </div>

          {recorder.problem && recorder.phase === 'idle' && (
            <p className="pc-hub-problem" role="alert">
              {ROLL_VOICE_PROBLEM[recorder.problem]}
            </p>
          )}
          {problem && (
            <div className="pc-alert" role="alert">
              <span style={{ flex: 1 }}>{problem.text}</span>
              {problem.kind === 'limit' && (
                <Link className="pc-btn" to="/club/subscription">
                  Подписка клуба
                </Link>
              )}
            </div>
          )}
          {!online && !problem && (
            <p className="pc-muted pc-hub-offline" role="status">
              Нет сети - перекличку не отправить. {audio ? 'Запись' : 'Текст'} останется на экране, отправьте, когда появится
              сеть.
            </p>
          )}
        </>
      )}
      {leave.dialog}
    </Screen>
  )
}

function RowChange({ row }: { row: RollCallRow }) {
  return (
    <span className="pc-change__what">
      {markText(row.before, row.beforeNotes)}
      <span aria-hidden="true"> → </span>
      <span className="pc-visually-hidden">, станет </span>
      {markText(row.after, row.afterNotes)}
    </span>
  )
}

function PreviewView({
  preview,
  outcome,
  saveError,
  online,
}: {
  preview: RollCallPreview & { heard: string }
  outcome: ConfirmOutcome | null
  saveError: 'offline' | 'server' | null
  online: boolean
}) {
  const changed = preview.rows.filter((r) => r.changed)
  const same = preview.rows.filter((r) => !r.changed)
  const unclear = preview.notFound.length + preview.ambiguous.length > 0

  return (
    <>
      <p className="pc-note" role="status">
        {preview.pendingActionId ? 'Пока ничего не записано. Проверьте список и подтвердите.' : 'Ничего не записано.'}
      </p>
      <p style={{ margin: '12px 0 0', overflowWrap: 'anywhere' }}>
        <span className="pc-muted">Текст: </span>«{preview.heard}»
      </p>

      {saveError && (
        <div className="pc-alert" role="alert">
          {saveError === 'offline' || !online
            ? 'Нет сети - не записано. Повторить безопасно: дважды не запишется.'
            : 'Не записалось. Повторить безопасно: дважды не запишется.'}
        </div>
      )}
      {outcome && !outcome.ok && (
        <div className="pc-alert" role="alert">
          {outcome.text}
        </div>
      )}

      {!preview.understood ? (
        <EmptyState
          title="Не нашёл переклички в тексте"
          text="Назовите имена и отметки, например: «Иванов есть, Петрова нет, остальных нет»."
        />
      ) : (
        <>
          {preview.mass && !preview.blocked && <p style={{ margin: '12px 0 0' }}>{MASS_TEXT[preview.mass]}</p>}

          {unclear && (
            <Unclear preview={preview}>
              {preview.blocked && preview.mass
                ? `${BLOCKED_TEXT[preview.mass]} Поэтому ничего не подготовлено: исправьте текст или отметьте вручную на экране занятия.`
                : 'Их отметки не изменятся: отметьте вручную на экране занятия или исправьте текст.'}
            </Unclear>
          )}

          {changed.length > 0 ? (
            <section className="pc-section" aria-label="Что изменится">
              <h2 className="pc-section-title">Изменится: {changed.length}</h2>
              <ul className="pc-list">
                {changed.map((r) => (
                  <li key={r.athleteId} className="pc-change">
                    <span className="pc-change__who">{r.name}</span>
                    <RowChange row={r} />
                  </li>
                ))}
              </ul>
            </section>
          ) : (
            !preview.blocked && <p style={{ margin: '12px 0 0' }}>Менять нечего: все названные уже отмечены так.</p>
          )}

          {same.length > 0 && !preview.blocked && (
            <section className="pc-section" aria-label="Без изменений">
              <h2 className="pc-section-title">Без изменений: {same.length}</h2>
              <ul className="pc-list">
                {same.map((r) => (
                  <li key={r.athleteId} className="pc-change">
                    <span className="pc-change__who">{r.name}</span>
                    <span className="pc-change__what">{markText(r.after, r.afterNotes)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </>
  )
}

/** Непонятые и неоднозначные имена: их отметить вручную. */
function Unclear({ preview, children }: { preview: RollCallPreview; children: ReactNode }) {
  return (
    <section className="pc-section" aria-label="Непонятые имена">
      <h2 className="pc-section-title">Не понял имена</h2>
      <ul className="pc-list">
        {preview.notFound.map((n) => (
          <li key={`nf-${n}`} className="pc-change">
            <span className="pc-change__who">«{n}»</span>
            <span className="pc-change__what">в группе не нашёл</span>
          </li>
        ))}
        {preview.ambiguous.map((a) => (
          <li key={`amb-${a.query}`} className="pc-change">
            <span className="pc-change__who">«{a.query}»</span>
            <span className="pc-change__what">подходит несколько: {a.candidates.join(', ')}</span>
          </li>
        ))}
      </ul>
      <p className="pc-muted" style={{ margin: '4px 0 0', fontSize: 14 }}>
        {children}
      </p>
    </section>
  )
}

function Done({ outcome, preview }: { outcome: ConfirmOutcome; preview: (RollCallPreview & { heard: string }) | null }) {
  const unclear = [...(preview?.notFound ?? []), ...(preview?.ambiguous ?? []).map((a) => a.query)]
  if (preview?.recorded) {
    // Короткая перекличка: итог сразу, непонятые - отдельно для ручной отметки
    const recorded = preview.rows.filter((r) => r.changed)
    return (
      <>
        <p className="pc-note" role="status">
          {outcome.text}
        </p>
        <p style={{ margin: '12px 0 0', overflowWrap: 'anywhere' }}>
          <span className="pc-muted">Текст: </span>«{preview.heard}»
        </p>
        <section className="pc-section" aria-label="Записано">
          <h2 className="pc-section-title">Записано: {recorded.length}</h2>
          <ul className="pc-list">
            {recorded.map((r) => (
              <li key={r.athleteId} className="pc-change">
                <span className="pc-change__who">{r.name}</span>
                <RowChange row={r} />
              </li>
            ))}
          </ul>
        </section>
        {unclear.length > 0 && <Unclear preview={preview}>Их не отметил: отметьте вручную на экране занятия.</Unclear>}
      </>
    )
  }
  return (
    <div className="pc-state" role="status">
      <h3>Готово</h3>
      <p>{outcome.text}</p>
      {unclear.length > 0 && (
        <p>Не понял и не отметил: {unclear.map((n) => `«${n}»`).join(', ')}. Их отметьте вручную на экране занятия.</p>
      )}
    </div>
  )
}
